// FILE: src/components/ui/VideoLightbox.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

/** Reusable fullscreen YouTube lightbox with smart playback strategy. */
function parseYouTubeId(urlOrId) {
  if (!urlOrId) return "";
  const s = String(urlOrId).trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(s)) return s;
  try {
    const u = new URL(s);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    if (u.searchParams.get("v")) return u.searchParams.get("v");
    const parts = u.pathname.split("/");
    const i = parts.indexOf("embed");
    if (i >= 0 && parts[i + 1]) return parts[i + 1];
  } catch {}
  return "";
}

// --- Only loaded/used when startMuted === true (needs API for unmute overlay) ---
let ytApiPromise = null;
function loadYouTubeAPI() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { prev && prev(); resolve(window.YT); };
    const s = document.createElement("script");
    s.src = "https://www.youtube.com/iframe_api";
    s.async = true;
    document.head.appendChild(s);
  });
  return ytApiPromise;
}

export default function VideoLightbox({
  open,
  onClose,
  url,
  title = "Video",
  startAt = 0,
  captions = false,
  startMuted = true,
}) {
  const vid = useMemo(() => parseYouTubeId(url), [url]);
  const [ready, setReady] = useState(false);
  const [muted, setMuted] = useState(!!startMuted);

  // API path refs
  const apiContainerRef = useRef(null);
  const playerRef = useRef(null);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Tear down player when closing (API path)
  const destroyPlayer = useCallback(() => {
    try {
      const p = playerRef.current;
      if (p?.stopVideo) p.stopVideo();
      if (p?.destroy) p.destroy();
    } catch {}
    playerRef.current = null;
    setReady(false);
    setMuted(!!startMuted);
  }, [startMuted]);

  // If muted: use IFrame API to show Tap-to-unmute overlay. If sound-on-start: skip API entirely.
  useEffect(() => {
    if (!open || !startMuted) return; // simple-embed path skips this effect
    let cancelled = false;
    (async () => {
      const YT = await loadYouTubeAPI();
      if (cancelled || !YT || !apiContainerRef.current || !vid) return;
      playerRef.current = new YT.Player(apiContainerRef.current, {
        host: "https://www.youtube.com", // standard host is least problematic
        width: "100%",
        height: "100%",
        videoId: vid,
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 1,
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          start: startAt || 0,
          cc_load_policy: captions ? 1 : 0,
          origin: window.location.origin,
        },
        events: {
          onReady: (ev) => {
            setReady(true);
            try {
              ev.target.playVideo?.();
            } catch {}
          },
        },
      });
    })();
    return () => { cancelled = true; destroyPlayer(); };
  }, [open, startMuted, vid, startAt, captions, destroyPlayer]);

  const handleUnmute = useCallback(() => {
    const p = playerRef.current;
    try {
      if (!p) return;
      p.unMute?.();
      p.setVolume?.(80);
      setMuted(false);
    } catch {}
  }, []);

  if (!open) return null;

  // Simple embed (sound-on-start path) — relies on the user's click that opened the modal.
  const simpleEmbedSrc =
    `https://www.youtube.com/embed/${vid}` +
    `?autoplay=1&controls=1&playsinline=1&rel=0&modestbranding=1` +
    (startAt ? `&start=${encodeURIComponent(startAt)}` : "") +
    (captions ? `&cc_load_policy=1` : "");

  return (
    <div
      className="vlb-bg"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="vlb-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="vlb-head">
          <div className="vlb-title">{title}</div>
          <div className="vlb-tools">
            <a
              href={`https://youtu.be/${vid}`}
              target="_blank"
              rel="noreferrer"
              className="vlb-link"
              title="Open on YouTube"
            >
              Open on YouTube ↗
            </a>
            <button className="vlb-btn" type="button" onClick={onClose}>Close</button>
          </div>
        </div>

        <div className="vlb-body">
          <div className="vlb-player" aria-busy={!ready && startMuted}>
            {startMuted ? (
              <>
                <div ref={apiContainerRef} className="vlb-iframe-slot" />
                {ready && muted && (
                  <button className="vlb-unmute" type="button" onClick={handleUnmute} title="Tap to unmute">
                    🔊 Tap to unmute
                  </button>
                )}
              </>
            ) : (
              // Simple embed: no API, avoids anti-bot prompt with sound-on-start
              <iframe
                className="vlb-iframe-slot"
                title={title}
                src={simpleEmbedSrc}
                allow="autoplay; fullscreen; picture-in-picture; encrypted-media; web-share"
                allowFullScreen
                // Note: omit referrerPolicy here so YouTube sees the page origin normally.
                style={{ border: 0 }}
                onLoad={() => setReady(true)}
              />
            )}
          </div>
        </div>
      </div>

      <style>{`
        .vlb-bg { position: fixed; inset: 0; z-index: 10000; background: rgba(0,0,0,.7); display: grid; place-items: center; padding: 10px; }
        .vlb-card { width: min(90vw, 1200px); height: min(85dvh, 720px); background: #111; color: #fff; border-radius: 14px; box-shadow: 0 10px 40px rgba(0,0,0,.35); display: grid; grid-template-rows: auto 1fr; overflow: hidden; }
        .vlb-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 12px; background: rgba(0,0,0,.35); backdrop-filter: blur(4px); }
        .vlb-title { font-weight: 800; letter-spacing: .2px; }
        .vlb-tools { display: flex; gap: 8px; align-items: center; }
        .vlb-link { color: #c7d2fe; text-decoration: none; font-weight: 700; font-size: 13px; }
        .vlb-link:hover { text-decoration: underline; }
        .vlb-btn { background: #e5e7eb; color: #111; border: none; border-radius: 10px; font-weight: 800; padding: 8px 12px; cursor: pointer; }
        .vlb-body { padding: 8px; display: grid; align-items: center; justify-items: center; }
        .vlb-player { position: relative; width: 100%; height: 100%; background: #000; border-radius: 10px; overflow: hidden; display: grid; place-items: center; }
        .vlb-iframe-slot { position: absolute; inset: 0; width: 100%; height: 100%; }
        .vlb-unmute { position: absolute; bottom: 12px; left: 12px; background: rgba(0,0,0,.55); color: #fff; border: 2px solid #fff3; border-radius: 999px; padding: 10px 14px; font-weight: 800; cursor: pointer; backdrop-filter: blur(2px); }
        @media (max-width: 640px) { .vlb-card { width: 96vw; height: 80dvh; } .vlb-unmute { bottom: 8px; left: 8px; padding: 8px 12px; } }
      `}</style>
    </div>
  );
}
